import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Home as HomeIcon, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { STATUS_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Kalender-Warnung: Heute Ankunft & Aufgaben noch offen.
 *
 * Rendert einen prominenten roten Banner ganz oben im Team-Dashboard, sobald
 * mindestens eine Buchung heute als Ankunftstag (`to_date == today`) endet
 * und in deren Checkliste (Arrival + Departure) noch offene Punkte stehen.
 *
 * Der Banner löst sich still auf, sobald alle Punkte abgehakt sind — er wird
 * NICHT sichtbar, wenn keine roten Ankünfte für heute anstehen.
 */
export default function TodayArrivalWarnings() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [b, p] = await Promise.all([
          api.get("/bookings"),
          api.get("/properties"),
        ]);
        if (cancelled) return;
        const today = new Date().toISOString().slice(0, 10);
        const propMap = Object.fromEntries((p.data || []).map((x) => [x.id, x]));
        const alerts = [];
        for (const bk of b.data || []) {
          if (bk.to_date !== today) continue;
          if (bk.status === "cancelled" || bk.status === "completed") continue;
          // WICHTIG: Kunden-Checklisten (arrival_checklist / departure_checklist)
          // beeinflussen die Warnung NICHT — nur der Team-getriebene
          // booking.status entscheidet, ob wir hier warnen. Der Backend-Cron
          // (`_send_team_arrival_warning_now`) liefert den exakten
          // Open-Count via `_all_booking_items_done` — das UI zeigt hier eine
          // kompakte Warnung ohne Zahl (die Detail-Übersicht öffnet den
          // Kalender, wo das Team die konkreten offenen Punkte sieht).
          const prop = propMap[bk.property_id] || {};
          alerts.push({
            id: bk.id,
            propertyName: prop.name || bk.property_id || "Objekt",
            city: prop.city || "",
          });
        }
        setItems(alerts);
      } catch (e) { /* silent — warning is non-critical UI */ }
    };
    load();
    // Refresh every 60 s so freshly-checked-off items dismiss the banner
    // without a manual page reload.
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (items.length === 0) return null;
  return (
    <div
      className="mb-8 border-2 rounded-lg overflow-hidden"
      style={{ borderColor: STATUS_COLORS.red, boxShadow: `0 0 0 4px ${withAlpha(STATUS_COLORS.red, 0.10)}` }}
      data-testid="today-arrival-warning"
    >
      <div
        className="px-4 py-2 text-ivory flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase font-semibold"
        style={{ background: STATUS_COLORS.red }}
      >
        <HomeIcon className="w-3.5 h-3.5" strokeWidth={2.4} />
        <span>Heute Ankunft · Aufgaben offen</span>
        <span className="ml-auto px-2 py-0.5 rounded-full bg-ivory/15 text-[10px]">
          {items.length}
        </span>
      </div>
      <ul className="divide-y divide-red-700/10 bg-white">
        {items.map((it) => (
          <li key={it.id} data-testid={`today-arrival-item-${it.id}`}>
            <Link
              to="/team/calendar"
              className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-red-50/60 transition-colors group"
            >
              <div className="min-w-0">
                <div className="font-display text-[16px] md:text-[17px] text-navy leading-tight truncate">
                  Heute Ankunft bei <span className="italic">{it.propertyName}</span>
                </div>
                <div className="text-[12px] text-red-800/85 mt-0.5">
                  Team hat noch offene Punkte in der Buchungs-Checkliste
                  {it.city && <span className="text-navy-dark/50"> · {it.city}</span>}
                </div>
              </div>
              <ChevronRight
                className="w-4 h-4 shrink-0 text-red-800/60 group-hover:translate-x-0.5 transition-transform"
                strokeWidth={2}
              />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
